import { DateOnly } from '@if/core/date'
import { EFinancingProgram, EPaymentFrequency, EPaymentPlan, getEProvinceList } from '@if/core/types'
import { formatCurrencyNoDecimals, formatDate } from '@if/core/utils'
import {
  ControlledDateOnlyPicker,
  ControlledDebouncedNumberField,
  ControlledSelect,
  FieldPrerequisiteSpec,
  FILTER_DEBOUNCE_MS,
  FieldReadyGate,
  FieldReadyGateProvider,
  FieldsetCard,
  FormContainer,
  Icon,
  renderSelectMenuItems,
  SplitContainer,
  useAsyncValidation,
  useReconcileFieldToOptions,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Alert, Box, Button, Grid, Stack, TextField, Typography } from '@mui/material'
import { Breadcrumb, MerchantAutocomplete } from '@src/components'
import { useResolvedFinancingConfig } from '@src/components/WorksheetForm'
import { useStickyContentTop } from '@src/containers/App/stickyChrome'
import {
  EProtectionPlanCoverage,
  useCalculatorEstimate,
  useCalculatorFirstPaymentOptions,
  type CalculatorEstimateResultDto,
} from '@src/data/api/calculator/calculator-api'
import { useGetMerchantById, useMerchantsForProgram } from '@src/data/api/merchant/merchant-api'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { Constants } from '@src/data/types/constants'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { resolveMaxLoanAmount } from '@src/services/loan-amount-limits'
import { FilePlus2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useFormState, useWatch, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CalculatorEstimateSummary from './components/CalculatorEstimateSummary'
import CalculatorPaymentDisplay from './components/CalculatorPaymentDisplay'
import CreditRatingSelector from './components/CreditRatingSelector'
import { DEFAULT_ERS_SCORE } from './hooks/creditRatingBands'
import { REGULAR_PLAN_ID, usePaymentPlanOptions, useTermOptions } from './hooks/standaloneCalculator-hooks'
import {
  standaloneCalculatorSchema,
  STANDALONE_CALCULATOR_DEFAULTS,
  type AmountBoundsContext,
  type StandaloneCalculatorValues,
} from './standaloneCalculatorSchema'

/**
 * A single-page calculator: one form, one request, one estimate. The backend resolves the rate
 * from the ERS, so no rate table is mirrored here.
 */

const useFormSetup = () => ({
  schema: standaloneCalculatorSchema,
  defaultValues: STANDALONE_CALCULATOR_DEFAULTS,
})

const MERCHANT_PREREQUISITES = [
  { name: 'financingProgramId', prefixKey: 'calculator', labelKey: 'financingProgram' },
] as const satisfies readonly FieldPrerequisiteSpec<StandaloneCalculatorValues, 'calculator'>[]

/** Joint is the only place a co-applicant matters here — it picks the coverage rate. */
const PROTECTION_PLAN_CHOICES = [
  { value: EProtectionPlanCoverage.None, labelKey: 'calculator.protectionPlanNone' },
  { value: EProtectionPlanCoverage.Single, labelKey: 'calculator.protectionPlanSingle' },
  { value: EProtectionPlanCoverage.Joint, labelKey: 'calculator.protectionPlanJoint' },
] as const

/** The plan list and its terms both hang off the merchant, so it has to be picked first. */
const MERCHANT_DEPENDENT_PREREQUISITES = [
  { name: 'merchantId', prefixKey: 'calculator', labelKey: 'merchant' },
] as const satisfies readonly FieldPrerequisiteSpec<StandaloneCalculatorValues, 'calculator'>[]

const StandaloneCalculatorPage = (): JSX.Element => {
  const { t } = useTranslation()
  const [result, setResult] = useState<CalculatorEstimateResultDto | null>(null)
  const estimate = useCalculatorEstimate()

  // Several requests can be in flight; only the newest may write the result.
  const latestRequest = useRef(0)

  const breadCrumbs = [
    { path: '/', label: t('breadcrumbs.home') },
    { path: '#', label: t('calculator.pageTitle') },
  ]

  // Filters mode submits on every change, so this gates when there is enough to quote.
  const compute = useCallback(
    async (values: StandaloneCalculatorValues) => {
      const { financingProgramId, merchantId, provinceIso, paymentPlanId, activationDate, amountRequested } = values
      if (!financingProgramId || !merchantId || !provinceIso || !paymentPlanId || !activationDate) return
      if (amountRequested == null) return

      const requestId = ++latestRequest.current
      const next = await estimate.mutateAsync({
        financingProgramId,
        request: {
          merchantId,
          ersScore: values.ersScore,
          provinceIso,
          amountRequested,
          term: values.term,
          paymentPlanId,
          paymentFrequency: values.paymentFrequency,
          deliveryOn: activationDate.toString(),
          firstPaymentOn: values.firstPaymentDate?.toString() ?? null,
          protectionPlan: values.protectionPlan,
        },
      })

      if (requestId === latestRequest.current) setResult(next)
    },
    [estimate],
  )

  return (
    <Stack spacing={3} sx={{ mb: 2 }}>
      <Breadcrumb trees={breadCrumbs} />
      <FormContainer useFormSetup={useFormSetup} mode="filters" onSubmit={compute} hideActions>
        {(methods) => (
          <StandaloneCalculatorFields
            methods={methods}
            result={result}
            isComputing={estimate.isPending}
            hasError={estimate.isError}
          />
        )}
      </FormContainer>
    </Stack>
  )
}

interface FieldsProps {
  methods: UseFormReturn<StandaloneCalculatorValues>
  result: CalculatorEstimateResultDto | null
  isComputing: boolean
  hasError: boolean
}

const StandaloneCalculatorFields = ({ methods, result, isComputing, hasError }: FieldsProps): JSX.Element => {
  const { t } = useTranslation()
  const featureFlags = useFeatureFlags()
  const { selectedCompany } = usePreferences()
  const { control, setValue } = methods
  const stickyTop = useStickyContentTop()

  const values = useWatch({ control })

  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')

  // B2c runs through a single merchant, so there is nothing to search for.
  const singleMerchantProgram = values.financingProgramId === EFinancingProgram.B2c ? EFinancingProgram.B2c : null
  const [programMerchants] = useMerchantsForProgram(singleMerchantProgram, selectedCompany)
  // Only lock when the program really does have one merchant; a second one re-opens the field.
  const lockedMerchant = singleMerchantProgram && programMerchants.length === 1 ? programMerchants[0] : null

  useEffect(() => {
    if (lockedMerchant && values.merchantId !== lockedMerchant.id) setValue('merchantId', lockedMerchant.id)
  }, [lockedMerchant, values.merchantId, setValue])

  // The merchant carries its own plans and, through them, its terms.
  const [merchant] = useGetMerchantById(values.merchantId ?? undefined)
  const paymentPlanOptions = usePaymentPlanOptions(merchant)

  const activationDate = values.activationDate ?? DateOnly.today()
  const paymentFrequency = values.paymentFrequency ?? EPaymentFrequency.Monthly

  const { options: termOptions, isLoading: isLoadingTerms } = useTermOptions({
    financingProgramId: values.financingProgramId ?? '',
    merchantId: values.merchantId ?? null,
    merchant,
    paymentPlanId: values.paymentPlanId ?? EPaymentPlan.RegularDailyInterests,
    amountRequested: values.amountRequested ?? null,
    provinceIso: values.provinceIso ?? '',
    includeProtection: (values.protectionPlan ?? EProtectionPlanCoverage.None) !== EProtectionPlanCoverage.None,
    interestRate: result?.interestRate ?? null,
    paymentFrequency,
    activationDate,
    firstPaymentDate: values.firstPaymentDate ?? null,
  })

  const programConfig = values.financingProgramId
    ? Constants.financingProgramConfigs[values.financingProgramId as EFinancingProgram]
    : undefined

  // Weekly/Bi-Weekly are Iceberg-only; iFinance programs are Monthly and lock the field.
  const supportedFrequencies = useMemo(
    () => programConfig?.supportedPaymentFrequencies ?? [EPaymentFrequency.Monthly],
    [programConfig],
  )

  // Programs that do not price on the ERS hide the tiles and drop the score.
  const pricesOnErs = programConfig?.pricesOnErs ?? false

  // Carries what the create-application page reads back off the query string.
  const createApplicationHref = useMemo(() => {
    if (!values.financingProgramId || !values.merchantId) return null
    const params = new URLSearchParams({ merchantId: values.merchantId })
    if (values.provinceIso) params.set('stateIso', values.provinceIso)
    if (values.amountRequested != null) params.set('requestedAmount', String(values.amountRequested))
    return `/Applications/${values.financingProgramId}/add?${params.toString()}`
  }, [values.financingProgramId, values.merchantId, values.provinceIso, values.amountRequested])

  // The merchant-merged config is the only source that answers for every program and follows a
  // merchant switch; the program constants only ever carried a fallback for two of them.
  const { financingConfig, isLoading: isLoadingFinancingConfig } = useResolvedFinancingConfig(
    (values.financingProgramId as EFinancingProgram) || undefined,
    values.merchantId || undefined,
  )

  // Personal holds non-medical files below the Medicard ceiling, keyed off the merchant's service
  // category — the same value the backend copies onto a new application's loan purpose.
  const amountBounds = useMemo<AmountBoundsContext>(
    () => ({
      min: financingConfig.minimumLoanAmount,
      max: resolveMaxLoanAmount(
        values.financingProgramId as EFinancingProgram,
        financingConfig.maximumLoanAmount,
        merchant?.serviceCategory,
      ),
    }),
    [financingConfig, values.financingProgramId, merchant?.serviceCategory],
  )

  useAsyncValidation('amountRequested', { loading: isLoadingFinancingConfig, data: amountBounds })

  // Both sides or nothing: a half-known range reads as a real limit of $0.
  const amountRange = useMemo(() => {
    if (amountBounds.min <= 0 || amountBounds.max <= 0) return null
    return {
      min: formatCurrencyNoDecimals(amountBounds.min),
      max: formatCurrencyNoDecimals(amountBounds.max),
    }
  }, [amountBounds])

  // Any invalid field blocks the recompute — `emit` gates `compute` on a whole-form `trigger()` —
  // so an invalid form means the figures on screen no longer match it.
  const { isValid } = useFormState({ control })

  // Bidirectional: no program on load would otherwise clear the default score for good.
  useEffect(() => {
    if (!pricesOnErs && values.ersScore != null) setValue('ersScore', null)
    else if (pricesOnErs && values.ersScore == null) setValue('ersScore', DEFAULT_ERS_SCORE)
  }, [pricesOnErs, values.ersScore, setValue])

  const paymentFrequencyOptions = useTranslatedOptionEnum('EPaymentFrequency', supportedFrequencies, 'enum')

  useEffect(() => {
    if (!supportedFrequencies.includes(paymentFrequency)) setValue('paymentFrequency', supportedFrequencies[0])
  }, [supportedFrequencies, paymentFrequency, setValue])

  // The program's own first-payment rules, same source as the worksheet's.
  const [firstPaymentOptions, isLoadingFirstPayment] = useCalculatorFirstPaymentOptions(
    values.financingProgramId
      ? {
          financingProgramId: values.financingProgramId,
          deliveryOn: activationDate.toString(),
          paymentFrequency,
        }
      : null,
  )

  const firstPaymentDates = useMemo(() => firstPaymentOptions.map((d) => new DateOnly(d)), [firstPaymentOptions])

  const firstPaymentMenuItems = useMemo(
    () => renderSelectMenuItems(firstPaymentDates, (d) => ({ value: d.toString(), label: formatDate(d) })),
    [firstPaymentDates],
  )

  // Changing the activation date or frequency re-rules the list, so a stale pick has to go.
  useEffect(() => {
    if (isLoadingFirstPayment || firstPaymentDates.length === 0) return
    const current = values.firstPaymentDate?.toString()
    if (!current || !firstPaymentDates.some((d) => d.toString() === current))
      setValue('firstPaymentDate', firstPaymentDates[0])
  }, [isLoadingFirstPayment, firstPaymentDates, values.firstPaymentDate, setValue])

  // Only "no terms" once answered, or the loading gap flashes a false error.
  const hasNoTerms = !isLoadingTerms && termOptions.length === 0
  const termHelperKey = isLoadingTerms
    ? 'calculator.loanTermLoading'
    : hasNoTerms
      ? 'calculator.loanTermUnavailable'
      : 'calculator.loanTermHelper'

  const paymentPlanMenuItems = useMemo(
    () =>
      renderSelectMenuItems(paymentPlanOptions, (option) => ({
        value: option.value,
        label:
          option.value === REGULAR_PLAN_ID
            ? t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })
            : option.label,
      })),
    [paymentPlanOptions, t],
  )

  const termMenuItems = useMemo(
    () =>
      renderSelectMenuItems(termOptions, (option) => ({
        value: option.value,
        label: t('calculator.termMonths', { count: option.value }),
      })),
    [termOptions, t],
  )

  // Joint prices at the co-applicant rate, so only programs that allow one offer it.
  const canHaveCoapplicant = programConfig?.canHaveCoapplicant ?? false

  const protectionPlanChoices = useMemo(
    () =>
      PROTECTION_PLAN_CHOICES.filter((choice) => canHaveCoapplicant || choice.value !== EProtectionPlanCoverage.Joint),
    [canHaveCoapplicant],
  )

  // Reset rather than clear: coverage always has an answer, and None is it.
  useEffect(() => {
    if (!canHaveCoapplicant && values.protectionPlan === EProtectionPlanCoverage.Joint)
      setValue('protectionPlan', EProtectionPlanCoverage.None)
  }, [canHaveCoapplicant, values.protectionPlan, setValue])

  const protectionPlanMenuItems = useMemo(
    () =>
      renderSelectMenuItems(protectionPlanChoices, (choice) => ({
        value: choice.value,
        label: t(choice.labelKey),
      })),
    [protectionPlanChoices, t],
  )

  // Default to the longest term, and re-anchor when a change strands the current one.
  useEffect(() => {
    if (isLoadingTerms || termOptions.length === 0) return
    if (values.term != null && termOptions.some((option) => option.value === values.term)) return
    setValue('term', Math.max(...termOptions.map((option) => option.value)))
  }, [isLoadingTerms, termOptions, values.term, setValue])

  const availableFinancingPrograms = useMemo(() => {
    return getProgramsByCompanyId(selectedCompany ? [selectedCompany] : [], featureFlags)
  }, [selectedCompany, featureFlags])

  const availableFinancingProgramMenuItems = useMemo(
    () =>
      renderSelectMenuItems(availableFinancingPrograms, (item) => ({
        value: item,
        label: t(`EFinancingProgram.${item}`, { ns: 'enum' }),
      })),
    [availableFinancingPrograms, t],
  )

  const { isValid: isFinancingProgramValid } = useReconcileFieldToOptions({
    control,
    name: 'financingProgramId',
    options: availableFinancingPrograms,
    getKey: (p) => p,
  })

  return (
    <FieldReadyGateProvider>
      {/* SplitContainer pins its aside 24px from the viewport top, which puts it under the
          app bar. Its offset is not a prop, so raise it from here. */}
      <Box sx={{ '& aside': { top: stickyTop } }}>
        <SplitContainer
          main={
            <Stack spacing={3}>
              <FieldsetCard title={t('calculator.parameters')} subtitle={t('calculator.estimateHint')}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <ControlledSelect
                      name="financingProgramId"
                      control={control}
                      label={t('calculator.financingProgram')}
                      options={isFinancingProgramValid ? availableFinancingProgramMenuItems : []}
                      disabled={!isFinancingProgramValid}
                      onChange={() => setValue('merchantId', null)}
                      disableEmptyOption
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <FieldReadyGate
                      control={control}
                      requiredFields={MERCHANT_PREREQUISITES}
                      selfName="merchantId"
                      shell={
                        <TextField
                          label={t('calculator.merchant')}
                          placeholder={t('calculator.merchantPlaceholder')}
                          disabled
                          fullWidth
                        />
                      }
                    >
                      {({ financingProgramId: program }) => (
                        <MerchantAutocomplete
                          control={control}
                          name="merchantId"
                          financingProgramId={program as EFinancingProgram}
                          label={t('calculator.merchant')}
                          disabled={!!lockedMerchant}
                        />
                      )}
                    </FieldReadyGate>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <ControlledSelect
                      name="provinceIso"
                      control={control}
                      label={t('calculator.province')}
                      options={provinceOptions}
                      disableEmptyOption
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    {/* Debounced: `filters` mode submits on change and applies no form-level
                        debounce, so a plain field fires one estimate per keystroke. */}
                    <ControlledDebouncedNumberField
                      name="amountRequested"
                      control={control}
                      label={t('calculator.requestedLoanAmount')}
                      debounceTime={FILTER_DEBOUNCE_MS}
                      isMoney
                      disableDecimals
                      fullWidth
                    />
                    {amountRange && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {t('calculator.requestedAmountHelper', amountRange)}
                      </Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FieldReadyGate
                      control={control}
                      requiredFields={MERCHANT_DEPENDENT_PREREQUISITES}
                      selfName="paymentPlanId"
                      shell={<TextField label={t('calculator.paymentPlanLabel')} disabled fullWidth />}
                    >
                      {() => (
                        <ControlledSelect
                          name="paymentPlanId"
                          control={control}
                          label={t('calculator.paymentPlanLabel')}
                          options={paymentPlanMenuItems}
                          onChange={() => setValue('term', null)}
                          disableEmptyOption
                          fullWidth
                        />
                      )}
                    </FieldReadyGate>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {t('calculator.paymentPlanHelper')}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FieldReadyGate
                      control={control}
                      requiredFields={MERCHANT_DEPENDENT_PREREQUISITES}
                      selfName="term"
                      shell={<TextField label={t('calculator.loanTermLabel')} disabled fullWidth />}
                    >
                      {() => (
                        <ControlledSelect
                          name="term"
                          control={control}
                          label={t('calculator.loanTermLabel')}
                          options={termMenuItems}
                          disabled={isLoadingTerms || hasNoTerms}
                          disableEmptyOption
                          fullWidth
                        />
                      )}
                    </FieldReadyGate>
                    <Typography
                      variant="caption"
                      color={hasNoTerms ? 'error' : 'text.secondary'}
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      {t(termHelperKey)}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <ControlledSelect
                      name="paymentFrequency"
                      control={control}
                      label={t('worksheetCommon.paymentFrequency')}
                      options={paymentFrequencyOptions}
                      disabled={supportedFrequencies.length <= 1}
                      disableEmptyOption
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <ControlledDateOnlyPicker
                      name="activationDate"
                      control={control}
                      label={t('worksheetCommon.activationDate')}
                      disablePast
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    {/* A select, not a picker: only the dates the program's rules allow are valid. */}
                    <ControlledSelect
                      name="firstPaymentDate"
                      control={control}
                      label={t('worksheetCommon.firstPaymentDate')}
                      options={firstPaymentMenuItems}
                      parseValue={(v) => (v ? new DateOnly(v) : null)}
                      disabled={isLoadingFirstPayment || firstPaymentDates.length === 0}
                      loading={isLoadingFirstPayment}
                      disableEmptyOption
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <ControlledSelect
                      name="protectionPlan"
                      control={control}
                      label={t('calculator.protectionPlan')}
                      options={protectionPlanMenuItems}
                      disableEmptyOption
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </FieldsetCard>

              {pricesOnErs && (
                <FieldsetCard title={t('calculator.creditRatingTitle')} subtitle={t('calculator.creditRatingHelp')}>
                  <CreditRatingSelector control={control} />
                </FieldsetCard>
              )}

              {hasError && <Alert severity="error">{t('calculator.estimateError')}</Alert>}

              <Typography variant="caption" color="text.secondary">
                {t('calculator.bestWayToKnow')}
              </Typography>
            </Stack>
          }
          aside={
            <Stack spacing={3}>
              <CalculatorPaymentDisplay
                isComputing={isComputing}
                monthlyEquivalent={isValid ? (result?.paymentForFrequency ?? null) : null}
                paymentFrequency={paymentFrequency}
                showProtectionPlanNote={isValid && (result?.protectionPlanAmount ?? 0) > 0}
              />
              {isValid && result && <CalculatorEstimateSummary result={result} />}
              {createApplicationHref && (
                <Button href={createApplicationHref} variant="outlined" startIcon={<Icon icon={FilePlus2} />}>
                  {t('calculator.createCreditApplication')}
                </Button>
              )}
            </Stack>
          }
        />
      </Box>
    </FieldReadyGateProvider>
  )
}

export default StandaloneCalculatorPage
